import { authFetch, BASE_BACKEND_URL, isAuthRedirectError } from '../auth'
import React, { useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { Skeleton, TableRow, TableCell, CircularProgress, useMediaQuery } from '@mui/material'
import {
  CalendarBlank,
  CaretDown,
  ClipboardText,
  ListChecks,
  DownloadSimple,
  Image,
  Trash,
  FileText,
  WarningCircle,
  Storefront,
  User,
  Package
} from '@phosphor-icons/react'
import cookie from 'react-cookies'
import { DEFAULT_FILTER_VALUE } from '../components/Filters/MuiFilterSelect'
import ReportFilters from '../components/Filters/ReportFilters'
import {
  hasReportSelection,
  serializeReportSelection
} from '../components/Filters/compactReportSelection'
import { buildSelectValue, getSelectValues } from '../components/Filters/filterSelectState'
import DataTable from '../components/table/DataTable'
import TableActionButtons from '../components/action_button/TableActionButtons'
import UbiquaTablePagination from '../components/table/UbiquaTablePagination'
import AppChip from '../components/chips/AppChip'
import AppListHeader from '../components/title/AppListHeader'
import UbiquaTowerToastAlert from '../components/alerts/UbiquaTowerToastAlert'
import DatePicker from 'react-multi-date-picker'
import Toolbar from 'react-multi-date-picker/plugins/toolbar'
import PosDownloadContext from './context/PosDownloadContext'
import dayjs from '../utils/dayjsConfig'
import { TASK_TYPES, TASK_TYPES_KEYS, POS_REPORT_PRODUCT_GROUPS } from './constants/constants'
import { DATE_PICKER_SETTINGS } from '../constants/constant'
import { UI_COLOR_SKELETON_BAR, UI_COLOR_DANGER, UI_COLOR_DANGER_SOFT } from '../constants/uiColors'

const OUT_OF_STOCK_OPTIONS = [
  { value: 'agotado', label: 'AGOTADO' },
  { value: 'no_agotado', label: 'NO AGOTADO' }
]

const TASK_ACHIEVED_OPTIONS = [
  { value: 'Si cumple', label: 'Si cumple' },
  { value: 'No cumple', label: 'No cumple' }
]

const DOWNLOAD_STATUS_TOOLTIPS = {
  empty: 'No hay resultados para los filtros seleccionados',
  error: 'Error'
}

const getSelectedFilterValues = (filterValue) =>
  filterValue?.mode === 'custom' ? filterValue.values || [] : []

export const omitFilterWhenAllSelected = (selectedValues, options) => {
  if (!options.length || selectedValues.length < options.length) return selectedValues
  const selectedSet = new Set(selectedValues)
  return options.every((option) => selectedSet.has(option.value)) ? [] : selectedValues
}

export default function PosDownloadView() {
  const isMobile = useMediaQuery('(max-width: 599px)')
  const {
    remoteFilterPages = {},
    configureReportFilters,
    onFilterSearch,
    onFilterOptionGroupChange,
    onFilterLoadMore,
    onFilterRetry,
    clientsData,
    usersData,
    surveysData,
    productTypeDic,
    reportsData,
    pagination,
    fetchReportsFromServer,
    onPageChange,
    onRowsPerPageChange,
    initialLoading,
    reportsError,
    fetchDataStatus,
    handleDeleteReport,
    fetchSurveysFromServer,
    taskType,
    setTaskType,
    isFetching,
    failedCreation,
    setFailedCreation,
    failedCreationMessage,
    setFailedCreationMessage
  } = useContext(PosDownloadContext)

  const jobStatusDic = {
    pending: 'En proceso...',
    created: 'Completado',
    error: 'Error',
    expired: 'Expirado',
    empty: 'Sin datos'
  }
  const [filterVersion, setFilterVersion] = useState(0)
  const [clientFilter, setClientFilter] = useState(DEFAULT_FILTER_VALUE)
  const [userFilter, setUserFilter] = useState(DEFAULT_FILTER_VALUE)
  const [productFilter, setProductFilter] = useState(DEFAULT_FILTER_VALUE)
  const [taskAchievedFilter, setTaskAchievedFilter] = useState(DEFAULT_FILTER_VALUE)
  const [outOfStockFilter, setOutOfStockFilter] = useState(DEFAULT_FILTER_VALUE)
  const [selectedSurvey, setSelectedSurvey] = useState(null)
  const [selectedReportDates, setSelectedReportDates] = useState(null)
  const [selectedExpirationDates, setSelectedExpirationDates] = useState(null)
  const [reportDateArray, setReportDateArray] = useState(null)
  const [expirationDateArray, setExpirationDateArray] = useState(null)
  const [optionalReportTitle, setOptionalReportTitle] = useState('')
  const [warning, setWarning] = useState(false)
  const [didFiltersChange, setDidFiltersChanged] = useState(true)
  const surveyNames = useRef(new Map())
  const [buttonDisabled, setButtonDisabled] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const reportCreationBaseline = useRef(null)
  const [newReportIds, setNewReportIds] = useState(() => new Set())

  useEffect(() => {
    const baseline = reportCreationBaseline.current
    if (!baseline) return
    if (
      reportsError ||
      baseline.page !== pagination?.current_page ||
      baseline.perPage !== pagination?.per_page
    ) {
      reportCreationBaseline.current = null
      return
    }
    const addedIds = (reportsData || [])
      .filter((report) => !baseline.ids.has(report.id))
      .map((report) => report.id)
    if (!addedIds.length) return
    reportCreationBaseline.current = null
    setNewReportIds(new Set(addedIds))
  }, [reportsData, reportsError, pagination?.current_page, pagination?.per_page])

  useEffect(() => {
    if (!newReportIds.size) return
    const timer = setTimeout(() => setNewReportIds(new Set()), 300)
    return () => clearTimeout(timer)
  }, [newReportIds])

  const isSurvey = taskType === TASK_TYPES.survey.key
  const clientSelected = hasReportSelection(clientFilter)
  const userSelected = hasReportSelection(userFilter)
  const usersDisabled = isSurvey && (!reportDateArray || !clientSelected)
  const surveysDisabled = isSurvey && (!reportDateArray || !clientSelected || !userSelected)
  const remoteConfig = (key) => ({
    loading: Boolean(remoteFilterPages[key]?.loading),
    loadingInTrigger: true,
    loadingLabel: {
      client: 'Clientes',
      user: 'Usuarios',
      product: 'Productos',
      survey: 'Encuestas'
    }[key],
    hasMore: Boolean(remoteFilterPages[key]?.hasMore),
    remoteSearchQuery: remoteFilterPages[key]?.search || '',
    error: remoteFilterPages[key]?.error,
    onRetry: () => onFilterRetry?.(key),
    ...(onFilterSearch ? { onSearchChange: (query) => onFilterSearch(key, query) } : {}),
    onLoadMore: () => onFilterLoadMore?.(key)
  })

  const clientOptions = useMemo(
    () =>
      remoteFilterPages.client?.options ||
      (clientsData || []).map((client) => ({
        value: client.uid,
        label: `${client.uid} - ${client.name}`
      })),
    [clientsData, remoteFilterPages.client]
  )

  const userOptions = useMemo(
    () =>
      remoteFilterPages.user?.options ||
      (usersData || []).map((user) => ({
        value: user.uid,
        label: `${user.uid} - ${user.name}`
      })),
    [usersData, remoteFilterPages.user]
  )

  const productGroups = useMemo(
    () =>
      (productTypeDic || []).map((group) => ({
        key: group.key,
        label: group.key,
        options: (group.value || []).map((optionLabel) => ({
          value: `${group.key}::${String(optionLabel).split(' - ')[0]}`,
          label: optionLabel
        }))
      })),
    [productTypeDic]
  )

  useEffect(() => {
    if (
      (taskType == TASK_TYPES.survey.key && selectedSurvey != null) ||
      taskType !== TASK_TYPES.survey.key
    ) {
      setButtonDisabled(false)
    } else if (taskType == TASK_TYPES.survey.key) {
      setButtonDisabled(true)
    }
    setDidFiltersChanged(true)
  }, [
    clientFilter,
    userFilter,
    productFilter,
    taskAchievedFilter,
    outOfStockFilter,
    reportDateArray,
    selectedSurvey,
    expirationDateArray,
    taskType
  ])

  useEffect(() => {
    setSelectedSurvey(null)
    surveyNames.current.clear()
    if (!isSurvey || !reportDateArray || !clientSelected || !userSelected) {
      fetchSurveysFromServer?.(null, null)
      return
    }
    fetchSurveysFromServer(
      {
        clients: serializeReportSelection(clientFilter),
        users: serializeReportSelection(userFilter)
      },
      reportDateArray
    )
  }, [clientFilter, userFilter, reportDateArray, taskType, fetchSurveysFromServer])

  useEffect(() => {
    for (const survey of surveysData || []) surveyNames.current.set(survey.uid, survey.name)
  }, [surveysData])

  useEffect(() => {
    let start = dayjs.unix(reportDateArray?.Start)
    let end = dayjs.unix(reportDateArray?.End)
    if (reportDateArray?.Start > 0 && reportDateArray?.End) {
      if (limitDateRange(start, end)) {
        setFailedCreation(true)
        setFailedCreationMessage(
          `Error: Solo puedes seleccionar un rango máximo de ${maxRangeLabel}.`
        )
      } else {
        setFailedCreation(false)
      }
    }
  }, [reportDateArray])

  useEffect(() => {
    setClientFilter(DEFAULT_FILTER_VALUE)
    setUserFilter(DEFAULT_FILTER_VALUE)
    setProductFilter(DEFAULT_FILTER_VALUE)
    setTaskAchievedFilter(DEFAULT_FILTER_VALUE)
    setOutOfStockFilter(DEFAULT_FILTER_VALUE)
    setReportDateArray(null)
    setSelectedReportDates(null)
    setExpirationDateArray(null)
    setSelectedExpirationDates(null)
    setSelectedSurvey(null)
    setFilterVersion((prev) => prev + 1)
    setButtonDisabled(taskType === TASK_TYPES.survey.key)
  }, [taskType])

  const { maxDays, maxRangeLabel } = useMemo(() => {
    if (taskType === TASK_TYPES.refund_limit.key) {
      return { maxDays: 366, maxRangeLabel: '366 días' }
    }

    return { maxDays: 31, maxRangeLabel: '31 días' }
  }, [taskType])

  useEffect(() => {
    const valid =
      reportDateArray &&
      Math.abs(dayjs.unix(reportDateArray.Start).diff(dayjs.unix(reportDateArray.End), 'days')) +
        1 <=
        maxDays
    configureReportFilters?.(taskType, valid ? reportDateArray : null)
  }, [taskType, reportDateArray, maxDays, configureReportFilters])

  const limitDateRange = (start, end) => {
    const daysDiff = Math.abs(start.diff(end, 'days')) + 1
    return daysDiff > maxDays
  }

  const buildReportQuery = (event) => {
    event.preventDefault()
    if (submitting) return
    setButtonDisabled(true)
    const taskAchieved = omitFilterWhenAllSelected(
      getSelectedFilterValues(taskAchievedFilter),
      TASK_ACHIEVED_OPTIONS
    )
    const outOfStock = omitFilterWhenAllSelected(
      getSelectedFilterValues(outOfStockFilter),
      OUT_OF_STOCK_OPTIONS
    )
    const clientArray = serializeReportSelection(clientFilter)
    const userArray = serializeReportSelection(userFilter)
    let surveyArray = []
    let surveyDescription = ''

    if (taskType == TASK_TYPES.survey.key) {
      const survey = selectedSurvey && {
        uid: selectedSurvey,
        name: surveyNames.current.get(selectedSurvey)
      }
      if (survey) {
        surveyArray.push(survey.uid)
        surveyDescription = survey.name
      }
    }

    const reportName = handleReportName()
    if (
      !taskType ||
      reportDateArray == null ||
      (isSurvey && (!clientSelected || !userSelected || surveyArray.length <= 0))
    ) {
      setFailedCreation(true)
      setFailedCreationMessage(
        'Por favor, complete los campos obligatorios marcados con * para continuar.'
      )
      setButtonDisabled(false)
    } else if (
      limitDateRange(dayjs.unix(reportDateArray?.Start), dayjs.unix(reportDateArray?.End))
    ) {
      setFailedCreation(true)
      setFailedCreationMessage(
        `Error: Solo puedes seleccionar un rango máximo de ${maxRangeLabel}.`
      )
      setButtonDisabled(false)
    } else if (!didFiltersChange) {
      setFailedCreation(true)
      setFailedCreationMessage(
        'Error: El reporte ya no se encuentra disponible para su descarga. Por favor, genere un nuevo reporte.'
      )
      setButtonDisabled(false)
    } else {
      const body = {
        business_uid: cookie.load('business_tenant_uid'),
        task_type: taskType,
        clients_db_refs: clientArray,
        users_db_refs: userArray,
        start_time: reportDateArray['Start'],
        end_time: reportDateArray['End'],
        product_selection: serializeReportSelection(productFilter),
        product_db_refs: [],
        brand_db_refs: [],
        category_db_refs: [],
        task_achieved: taskAchieved,
        out_of_stock: outOfStock,
        task_uuid: surveyArray,
        task_description: surveyDescription,
        created_date: new Date(),
        report_name: reportName,
        expiration_start: expirationDateArray?.Start ?? null,
        expiration_end: expirationDateArray?.End ?? null
      }
      createNewReport(body)
    }
  }

  const createNewReport = async (body) => {
    setSubmitting(true)
    try {
      const response = await authFetch(`${BASE_BACKEND_URL}/pos_downloads`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      if (response) await evaluateResponse(response)
    } catch (error) {
      if (isAuthRedirectError(error)) return
      setFailedCreation(true)
      setFailedCreationMessage('No se pudo generar el reporte. Intenta de nuevo.')
    } finally {
      setSubmitting(false)
      setButtonDisabled(false)
    }
  }

  const evaluateResponse = async (response) => {
    setButtonDisabled(false)
    if (response.status === 422) {
      setFailedCreation(true)
      const obj = await response.json()
      setFailedCreationMessage(obj.error || 'No se pudo generar el reporte. Revisa los filtros.')
    } else if (response.status === 500) {
      setFailedCreation(true)
      setFailedCreationMessage('Error al crear nuevo reporte. Por favor, edita los parametros.')
    } else if (response.status === 404) {
      setFailedCreation(true)
      setFailedCreationMessage('Hay un error en creacion de reportes, verifique la información.')
    } else if (response.status === 204) {
      setFailedCreation(true)
      setFailedCreationMessage(
        'Error: No es posible generar el reporte. No se encontraron resultados con los filtros seleccionados. Por favor, ajusta los filtros y vuelve a intentarlo para poder generar el reporte.'
      )
    } else if (response.ok) {
      reportCreationBaseline.current = {
        ids: new Set((reportsData || []).map((report) => report.id)),
        page: pagination?.current_page,
        perPage: pagination?.per_page
      }
      fetchReportsFromServer(true)
      setDidFiltersChanged(false)
    } else {
      console.log('error: ', response)
    }
  }

  const fetchDownload = async (model, type = 'csv') => {
    let modelDate = new Date(model.due_date)

    if (modelDate <= new Date()) {
      setFailedCreation(true)
      setFailedCreationMessage(
        'Error: El reporte ya no se encuentra disponible para su descarga. Por favor, genere un nuevo reporte.'
      )
      return
    }

    try {
      const response = await authFetch(`${BASE_BACKEND_URL}/pos_downloads/${model.id}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (!response || !response.ok) {
        setFailedCreation(true)
        setFailedCreationMessage('Error: No se pudo procesar la solicitud de descarga.')
        return
      }

      const data = await response.json()

      if (model.status === 'empty') {
        setWarning(true)
      }

      const reportData = data?.pos_download_reports?.[0]

      const url =
        type === 'images'
          ? reportData?.images_url
          : reportData?.report_url

      if (!url) {
        setFailedCreation(true)
        setFailedCreationMessage('Error: No se puede obtener el reporte')
        return
      }

      window.open(url)
    } catch (error) {
      if (isAuthRedirectError(error)) return
      console.error('Download error: ', error)
      setFailedCreation(true)
      setFailedCreationMessage('Error: No se puede obtener el archivo.')
    }
  }

  const handleReportName = () => {
    let name = 'Analisis_PDV_' + TASK_TYPES[taskType].downloadName
    if (optionalReportTitle.length > 0) {
      name = optionalReportTitle
    }
    return `${name}_${dayjs().format('DD-MM-YYYY_HHmmss')}.csv`
  }

  const clearPeriodSelections = () => {
    setClientFilter(DEFAULT_FILTER_VALUE)
    setUserFilter(DEFAULT_FILTER_VALUE)
    setProductFilter(DEFAULT_FILTER_VALUE)
    setTaskAchievedFilter(DEFAULT_FILTER_VALUE)
    setOutOfStockFilter(DEFAULT_FILTER_VALUE)
    setSelectedSurvey(null)
    setFilterVersion((version) => version + 1)
  }

  const handleDateFilter = (datesObj, dateType) => {
    // El rango se confirma con el segundo clic, incluso si ambos clics son sobre el mismo día.
    // El primer clic queda como selección provisional del calendario, sin consultar ni reiniciar los filtros.
    if (Array.isArray(datesObj) && datesObj.length === 1) return

    const REPORT = 'report'
    const EXPIRATION = 'expiration'
    let formatedDates = []
    let startDate = new Date()
    let endDate = new Date()

    if (!datesObj || datesObj.length == 0) {
      if (dateType === REPORT) {
        clearPeriodSelections()
        setReportDateArray(null)
      }
      if (dateType === EXPIRATION) {
        setExpirationDateArray(null)
      }
    } else {
      datesObj.forEach((date) => {
        formatedDates.push(date.unix)
      })
      if (formatedDates.length > 1) {
        startDate = Math.min(formatedDates[0], formatedDates[1])
        endDate = Math.max(formatedDates[0], formatedDates[1])
      }
      if (dateType === REPORT) {
        const period = {
          Start: dayjs.unix(startDate).startOf('day').unix(),
          End: dayjs.unix(endDate).endOf('day').unix()
        }
        if (period.Start !== reportDateArray?.Start || period.End !== reportDateArray?.End) {
          clearPeriodSelections()
          setReportDateArray(period)
        }
      }
      if (dateType === EXPIRATION) {
        setExpirationDateArray({
          Start: dayjs.unix(startDate).startOf('day').unix(),
          End: dayjs.unix(endDate).endOf('day').unix()
        })
      }
    }
  }

  const resetFilters = () => {
    setReportDateArray(null)
    setSelectedReportDates(null)
    setExpirationDateArray(null)
    setSelectedExpirationDates(null)
    setSelectedSurvey(null)
    setClientFilter(DEFAULT_FILTER_VALUE)
    setUserFilter(DEFAULT_FILTER_VALUE)
    setProductFilter(DEFAULT_FILTER_VALUE)
    setTaskAchievedFilter(DEFAULT_FILTER_VALUE)
    setOutOfStockFilter(DEFAULT_FILTER_VALUE)
    setFilterVersion((prev) => prev + 1)
    setTaskType(null)
    setWarning(false)
    setFailedCreation(false)
    setFailedCreationMessage('')
    setDidFiltersChanged(true)
    setButtonDisabled(false)
  }

  const showClientAndUserFilters =
    taskType != null &&
    taskType != TASK_TYPES.client_application.key &&
    taskType != TASK_TYPES.users_start_end_day.key &&
    taskType != TASK_TYPES.refund_limit.key

  const showUserOnlyFilters = [
    TASK_TYPES.client_application.key,
    TASK_TYPES.users_start_end_day.key,
    TASK_TYPES.refund_limit.key
  ].includes(taskType)

  const showProductFilter = [
    TASK_TYPES.inventory.key,
    TASK_TYPES.planimetry.key,
    TASK_TYPES.expiration_date.key
  ].includes(taskType)

  const filterSelections = {
    type: buildSelectValue(taskType ? [taskType] : []),
    survey: buildSelectValue(selectedSurvey ? [selectedSurvey] : []),
    client: clientFilter,
    user: userFilter,
    product: productFilter,
    achieved: taskAchievedFilter,
    stock: outOfStockFilter
  }
  const filterSetters = {
    type: (value) => setTaskType(getSelectValues(value)[0] || null),
    survey: (value) => setSelectedSurvey(getSelectValues(value)[0] || null),
    client: (value) => {
      setClientFilter(value)
      if (isSurvey) {
        setUserFilter(DEFAULT_FILTER_VALUE)
        setSelectedSurvey(null)
      }
    },
    user: setUserFilter,
    product: setProductFilter,
    achieved: setTaskAchievedFilter,
    stock: setOutOfStockFilter
  }
  const reportFilters = [
    {
      key: 'type',
      label: 'Tipo de tarea',
      icon: ClipboardText,
      variant: 'report-type',
      singleSelect: true,
      alwaysInline: true,
      showInSummary: false,
      required: true,
      options: TASK_TYPES_KEYS.map((key) => ({ value: key, label: TASK_TYPES[key].name }))
    },

    ...(showClientAndUserFilters
      ? [
          {
            key: 'client',
            label: 'Cliente',
            icon: Storefront,
            options: clientOptions,
            globalSelection: true,
            required: isSurvey,
            ...remoteConfig('client'),
            disabledReason: !reportDateArray ? 'Selecciona la fecha del reporte.' : undefined,
            disabled: !reportDateArray,
            alwaysInline: true
          }
        ]
      : []),
    ...(showClientAndUserFilters || showUserOnlyFilters
      ? [
          {
            key: 'user',
            label: 'Usuario',
            icon: User,
            alwaysInline: true,
            options: userOptions,
            globalSelection: true,
            selectAllLabel:
              taskType === TASK_TYPES.refund_limit.key
                ? 'Seleccionar todos los usuarios'
                : undefined,
            required: isSurvey,
            ...remoteConfig('user'),
            disabledReason: usersDisabled
              ? 'Selecciona clientes para habilitar los usuarios.'
              : !reportDateArray
                ? 'Selecciona la fecha del reporte.'
                : undefined,
            disabled: !reportDateArray || usersDisabled
          }
        ]
      : []),
    ...(showProductFilter
      ? [
          {
            key: 'product',
            label: 'Productos',
            icon: Package,
            disabled: !reportDateArray,
            globalSelection: true,
            ...remoteConfig('product'),
            optionGroups: POS_REPORT_PRODUCT_GROUPS,
            activeOptionGroup: remoteFilterPages.product?.optionGroup,
            onOptionGroupChange: onFilterOptionGroupChange
              ? (group) => onFilterOptionGroupChange('product', group)
              : undefined,
            options:
              remoteFilterPages.product?.options ||
              productGroups.flatMap((group) =>
                group.options.map((option) => ({
                  ...option,
                  label: `${group.label}: ${option.label}`
                }))
              )
          }
        ]
      : []),
    ...(taskType === TASK_TYPES.planimetry.key
      ? [
          {
            key: 'achieved',
            label: 'Cumple tarea',
            options: TASK_ACHIEVED_OPTIONS,
            disabled: !reportDateArray
          }
        ]
      : []),
    ...(taskType === TASK_TYPES.power_sku.key
      ? [
          {
            key: 'stock',
            label: 'Agotado',
            options: OUT_OF_STOCK_OPTIONS,
            disabled: !reportDateArray
          }
        ]
      : []),
    ...(taskType === TASK_TYPES.survey.key
      ? [
          {
            key: 'survey',
            label: 'Encuesta',
            icon: ListChecks,
            variant: 'report-survey',
            singleSelect: true,
            alwaysInline: true,
            showInSummary: false,
            required: true,
            ...remoteConfig('survey'),
            disabledReason: surveysDisabled
              ? 'Selecciona fecha, cliente y usuario para habilitar las encuestas.'
              : undefined,
            disabled: surveysDisabled,
            options: (surveysData || []).map((survey) => ({
              value: survey.uid,
              label: survey.name || survey.uid
            }))
          }
        ]
      : [])
  ]
  const hasRefinements = reportFilters.some((filter) =>
    hasReportSelection(filterSelections[filter.key])
  )
  const applyRefinements = (selections) => {
    const nextType = getSelectValues(selections.type)[0] || null
    if (nextType !== taskType) {
      setTaskType(nextType)
      return
    }
    Object.entries(selections).forEach(([key, value]) => filterSetters[key]?.(value))
  }
  const validDates =
    reportDateArray &&
    !limitDateRange(dayjs.unix(reportDateArray.Start), dayjs.unix(reportDateArray.End))
  const surveyLoading = taskType === TASK_TYPES.survey.key && fetchDataStatus?.surveys
  const canGenerate =
    taskType &&
    validDates &&
    !submitting &&
    !buttonDisabled &&
    (!isSurvey ||
      (selectedSurvey && clientSelected && userSelected && !surveysDisabled && !surveyLoading))
  const getMissingRequirement = () => {
    switch (true) {
      case !taskType:
        return 'Selecciona el tipo de tarea.'
      case !reportDateArray:
        return 'Selecciona la fecha del reporte.'
      case !validDates:
        return `Selecciona un período de hasta ${maxRangeLabel}.`
      case !isSurvey:
        return null
      case !clientSelected:
        return 'Selecciona clientes en los filtros para habilitar los usuarios.'
      case !userSelected:
        return 'Selecciona al menos un usuario o todos los usuarios del período.'
      case Boolean(surveyLoading):
        return 'Cargando encuestas del período…'
      case Boolean(remoteFilterPages.survey?.error):
        return 'No se pudieron cargar las encuestas. Abre el filtro para volver a intentar.'
      case !selectedSurvey && remoteFilterPages.survey?.page > 0 && !surveysData?.length:
        return 'No hay encuestas para el período, clientes y usuarios seleccionados. Ajusta los filtros.'
      case !selectedSurvey:
        return 'Selecciona una encuesta para generar el reporte.'
      default:
        return null
    }
  }
  const missingRequirement = getMissingRequirement()
  const closeError = useCallback(() => setFailedCreation(false), [setFailedCreation])
  const closeWarning = useCallback(() => setWarning(false), [])
  const columns = [
    {
      key: 'name',
      label: 'Nombre del reporte',
      width: '41%',
      truncate: true,
      getTitle: (report) => report.name,
      render: (report) => <span className="ubiqua-table-title">{report.name || '—'}</span>
    },
    {
      key: 'task_type',
      label: 'Tipo de tarea',
      width: '23%',
      truncate: true,
      render: (report) => (
        <AppChip
          label={TASK_TYPES[report.task_type]?.description || report.task_type || '—'}
          variant="info"
        />
      )
    },
    {
      key: 'period',
      label: 'Período del reporte',
      width: '24%',
      render: (report) =>
        `${dayjs(report.start_date).format('DD/MM/YYYY')} – ${dayjs(report.end_date).format('DD/MM/YYYY')}`
    },
    {
      key: 'status',
      label: 'Estado',
      width: 148,
      render: (report) => (
        <span className="pos-report-status">
          <AppChip
            className="app-chip--status"
            label={
              report.status === 'pending' ? (
                <span className="pos-report-status-label">
                  <CircularProgress size={10} color="inherit" aria-label="Reporte en proceso" />
                  {jobStatusDic.pending}
                </span>
              ) : (
                jobStatusDic[report.status] || report.status
              )
            }
            variant={
              {
                pending: 'info',
                created: 'success',
                error: 'danger',
                expired: 'neutral',
                empty: 'warning'
              }[report.status] || 'neutral'
            }
          />
        </span>
      )
    }
  ]
  const renderDatePicker = (expiration = false) => {
    const range = expiration ? expirationDateArray : reportDateArray
    const label = expiration ? 'Vencimiento' : 'Fecha de reporte'
    return (
      <div
        className={`report-filters__date${expiration ? ' report-filters__date--expiration' : ''}`}
      >
        <DatePicker
          disabled={submitting}
          range
          rangeHover
          arrow={false}
          offsetY={8}
          className="report-period-calendar"
          highlightToday={false}
          numberOfMonths={isMobile ? 1 : 2}
          plugins={[<Toolbar key="toolbar" position="bottom" sort={[]} />]}
          format="DD MMM"
          dateSeparator=" – "
          months={DATE_PICKER_SETTINGS.months}
          weekDays={DATE_PICKER_SETTINGS.weekDays}
          weekStartDayIndex={DATE_PICKER_SETTINGS.weekStartDayIndex}
          maxDate={expiration ? undefined : dayjs().endOf('day').toDate()}
          value={expiration ? selectedExpirationDates : selectedReportDates}
          onChange={(dates) => {
            handleDateFilter(dates, expiration ? 'expiration' : 'report')
            if (expiration) setSelectedExpirationDates(dates)
            else setSelectedReportDates(dates)
          }}
          render={(_, openCalendar) => (
            <button
              type="button"
              className={`filter-ubiqua-mui-filter-trigger pos-report-date-trigger${range ? ' has-value' : ''}`}
              aria-label={expiration ? 'Fecha de vencimiento' : 'Período del reporte'}
              aria-haspopup="dialog"
              title={
                range
                  ? `${label}: ${dayjs.unix(range.Start).format('DD/MM/YYYY')} – ${dayjs.unix(range.End).format('DD/MM/YYYY')}`
                  : label
              }
              disabled={submitting}
              onClick={openCalendar}
            >
              <CalendarBlank size={16} aria-hidden="true" />
              <span className="filter-ubiqua-mui-filter-value">
                {range ? (
                  `${dayjs.unix(range.Start).format('DD MMM')} – ${dayjs.unix(range.End).format('DD MMM')}`
                ) : (
                  <>
                    {label}
                    {!expiration && <span className="filter-ubiqua-mui-required"> *</span>}
                  </>
                )}
              </span>
              <CaretDown
                className="filter-ubiqua-mui-filter-chevron"
                size={14}
                weight="bold"
                aria-hidden="true"
              />
            </button>
          )}
        />
      </div>
    )
  }

  return (
    <div className="app-list-shell ubiqua-type pos-report-page app-content-enter">
      <AppListHeader
        title="Reportes de tareas en punto de venta"
        subtitle="Personalice y descargue reportes. Ajuste los filtros según la configuración previa para obtener datos específicos."
      >
        <UbiquaTowerToastAlert
          snackbarClassName="pos-report-toast"
          open={failedCreation}
          id="pos-report-error"
          title="No se pudo completar la solicitud"
          message={failedCreationMessage}
          onClose={closeError}
          icon={<WarningCircle size={20} />}
          colors={{ iconBackground: UI_COLOR_DANGER_SOFT, progress: UI_COLOR_DANGER }}
        />
        <UbiquaTowerToastAlert
          snackbarClassName="pos-report-toast"
          open={warning}
          id="pos-report-warning"
          title="Reporte sin datos"
          message="El archivo descargado no contiene datos. Revisa los filtros y genera un nuevo reporte."
          icon={<WarningCircle size={20} />}
          onClose={closeWarning}
        />
      </AppListHeader>
      <div className="index-table app-list-content">
        <ReportFilters
          autoApply
          filterKey={`${taskType}-${filterVersion}`}
          filters={reportFilters}
          selections={filterSelections}
          hasSelections={hasRefinements}
          disabled={submitting}
          canGenerate={Boolean(canGenerate)}
          generating={submitting}
          reportTitle={optionalReportTitle}
          onReportTitleChange={setOptionalReportTitle}
          requirement={missingRequirement}
          hasConfiguration={Boolean(hasRefinements || reportDateArray || expirationDateArray)}
          onFilterChange={(key, value) => filterSetters[key]?.(value)}
          onApplyFilters={applyRefinements}
          onReset={resetFilters}
          onGenerate={buildReportQuery}
          dateControls={
            taskType ? (
              <>
                {renderDatePicker()}
                {taskType === TASK_TYPES.expiration_date.key && renderDatePicker(true)}
              </>
            ) : null
          }
        />
        <section aria-labelledby="pos-reports-title" className="pos-report-results">
          <div className="pos-report-section-heading">
            <h3 id="pos-reports-title">Reportes generados</h3>
            <span>Disponibles durante 24 horas</span>
          </div>
          <div className="app-list-table-card">
            {reportsError ? (
              <div className="pos-report-load-error" role="alert">
                <p>{reportsError}</p>
                <button
                  type="button"
                  onClick={() => fetchReportsFromServer()}
                  disabled={isFetching}
                >
                  Volver a intentar
                </button>
              </div>
            ) : (
              <DataTable
                ariaLabel="Reportes de tareas en punto de venta"
                columns={columns}
                rows={reportsData || []}
                rowKey="id"
                getRowClassName={(report) =>
                  newReportIds.has(report.id) ? 'pos-report-row--new' : undefined
                }
                loading={isFetching || initialLoading}
                actionsEnabled
                actionColumnWidth={144}
                renderRowActions={(report) => (
                  <TableActionButtons
                    gap={0.75}
                    sx={{
                      justifyContent: 'center',
                      flexWrap: 'nowrap',
                      '& svg': { display: 'block !important', margin: 0 }
                    }}
                    utilityActions={[
                      {
                        label: 'Descargar reporte',
                        tooltip: DOWNLOAD_STATUS_TOOLTIPS[report.status] || 'Descargar reporte',
                        iconNode: <DownloadSimple size={17} />,
                        disabled: report.status !== 'created',
                        onClick: () => fetchDownload(report, 'csv'),
                        sx: {
                          width: 30,
                          height: 30,
                          borderRadius: '8px',
                          color: '#27588D',
                          backgroundColor: '#F2F7FC',
                          borderColor: '#D3E2F2',
                          '&:hover': {
                            color: '#1E4671',
                            backgroundColor: '#E4EFFA',
                            borderColor: '#AFCBE7'
                          }
                        }
                      },
                      {
                        label: 'Descargar imágenes',
                        tooltip: DOWNLOAD_STATUS_TOOLTIPS[report.status] || 'Descargar imágenes',
                        visible: Boolean(report.has_images_task),
                        iconNode: <Image size={17} />,
                        disabled: report.status !== 'created',
                        onClick: () => fetchDownload(report, 'images'),
                        sx: {
                          width: 30,
                          height: 30,
                          borderRadius: '8px',
                          color: '#218C80',
                          backgroundColor: '#EAF9F5',
                          borderColor: '#BCE5DC',
                          '&:hover': {
                            color: '#176C63',
                            backgroundColor: '#D6F2EB',
                            borderColor: '#89CDBF'
                          }
                        }
                      },
                      {
                        label: 'Eliminar reporte',
                        iconNode: <Trash size={17} />,
                        disabled: report.status === 'pending',
                        onClick: (event) => handleDeleteReport(report, event),
                        sx: {
                          width: 30,
                          height: 30,
                          borderRadius: '8px',
                          color: UI_COLOR_DANGER,
                          backgroundColor: '#FFF6F6',
                          borderColor: '#F7D5D7',
                          '&:hover': {
                            color: UI_COLOR_DANGER,
                            backgroundColor: UI_COLOR_DANGER_SOFT,
                            borderColor: '#F5A3A3'
                          }
                        }
                      }
                    ]}
                  />
                )}
                renderSkeletonRows={() =>
                  Array.from({ length: 5 }, (_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: 5 }, (_, cell) => (
                        <TableCell key={cell}>
                          <Skeleton
                            animation="wave"
                            height={20}
                            sx={{ bgcolor: UI_COLOR_SKELETON_BAR }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                }
                emptyState={{
                  title: 'Aún no hay reportes generados',
                  description:
                    'Selecciona el tipo de tarea y el período para generar tu primer reporte. Los archivos estarán disponibles aquí durante 24 horas.',
                  icon: <FileText size={28} />
                }}
              />
            )}
            <p className="pos-report-scroll-hint">
              Desliza la tabla para ver el estado y las acciones.
            </p>
            {Number(pagination?.total_count) > 10 && (
              <UbiquaTablePagination
                pagination={pagination}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                itemLabel="reportes"
                emptyLabel="Sin reportes para mostrar"
                ariaLabel="Paginación de reportes"
                disabled={isFetching || initialLoading}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  )
}
